import { useMemo } from 'react';
import { isObject } from 'lodash-es';
import { omitEmptyQueryParams } from '@shared/utils/omitEmptyQueryParams';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';

/**
 * Every key copied onto `extraPageInfo` of a guest-site page view. This list — not
 * `PartialUtmParameters` — is why Amplitude's `PageViewed` carries `extraPageInfo.utm_medium`,
 * `utm_campaign` and `utm_source` and nothing else, so a UTM slot must be added here to be
 * groupable in Amplitude without substring-matching `search`. See TRAV-4212.
 */
const supportedKeys = ['type', 'utm_medium', 'utm_campaign', 'utm_source', 'utm_content', 'guestid', 'batchid', 'slug'] as const;

export type GuestSiteQueryParams = Partial<Record<(typeof supportedKeys)[number], string>>;

export const useGuestSiteQueryParams = (): GuestSiteQueryParams => {
  const queryParams = useQueryParams();

  const paramsFromQuery = useMemo(() => {
    if (!isObject(queryParams)) {
      return {};
    }

    return supportedKeys.reduce((acc, key) => {
      const value = queryParams[key];
      if (typeof value === 'string' && value !== 'null') {
        acc[key] = value;
      }
      return acc;
    }, {} as GuestSiteQueryParams);
  }, [queryParams]);

  return omitEmptyQueryParams(paramsFromQuery);
};
